import {useAppVersions} from '../hooks/useAppVersions';
import {useTraduire} from '../i18n/langue';
import {T} from '../i18n/textes';
import {ALL_PLATFORMS, AppPlatform, PLATFORM_DISPLAY_NAMES} from '../types/feedback';
import {ClockCounterClockwise, DeviceMobile, Storefront} from '@phosphor-icons/react';

const STORE_URLS: Record<AppPlatform, string | null> = {
    ios: 'https://apps.apple.com/us/app/assimil-learn-languages/id1438616898',
    android: 'https://play.google.com/store/apps/details?id=com.mantano.assimil',
    macos: 'https://apps.apple.com/us/app/assimil-learn-languages/id1438616898',
    windows: 'https://apps.microsoft.com/detail/9mw6hjz8456q?hl=fr-FR&gl=FR',
    web: 'https://online.assimil.app/',
};

export function AppVersionsPage() {
    const t = useTraduire();
    const {storeVersions, loading, error, refresh} = useAppVersions();

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-3 text-gray-600">{t(T.chargementVersions)}</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <p className="text-red-800">{error}</p>
                <button
                    onClick={refresh}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-medium text-gray-900 flex items-center gap-2">
                        <DeviceMobile size={26} weight="regular" className="text-primary"/>
                        App Versions
                    </h1>
                    <p className="text-gray-600 mt-1">
                        Latest app versions detected from the stores.
                    </p>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Platform
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Market Version
                        </th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {ALL_PLATFORMS.map((platform) => (
                        <tr key={platform}>
                            <td className="px-6 py-4 whitespace-nowrap">
                                {STORE_URLS[platform] ? (
                                    <a
                                        href={STORE_URLS[platform]!}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 text-sm font-medium text-gray-900 hover:text-primary transition-colors"
                                        title={`View on store`}
                                    >
                                        <Storefront size={18} weight="regular" className="text-primary"/>
                                        {PLATFORM_DISPLAY_NAMES[platform]}
                                    </a>
                                ) : (
                                    <span className="text-sm font-medium text-gray-900">
                                        {PLATFORM_DISPLAY_NAMES[platform]}
                                    </span>
                                )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                {storeVersions[platform] ? (
                                    platform === 'web' ? (
                                        <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                                            <ClockCounterClockwise size={16} weight="regular"
                                                                   className="text-gray-400"/>
                                            {storeVersions[platform]}
                                        </span>
                                    ) : (
                                        <span className="text-sm text-gray-900">
                                            {storeVersions[platform]}
                                        </span>
                                    )
                                ) : (
                                    <span className="text-sm text-gray-400 italic">—</span>
                                )}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
